import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Form,
    Input,
    InputNumber,
    Button,
    Space,
    Typography,
    message,
    Select,
} from 'antd';
import { AppInfo } from '../../types/app-info.types';
import { appInfoService } from '../../services/AppInfoService';
import { OperationStatus } from '../../types/api.types';

const { Title } = Typography;
const { Option } = Select;

export interface AppInfoBulkEditRequest {
    appNames: string[];
    version?: string;
    versionCode?: number;
    downloadLink?: string;
    adminComponentName?: string;
    clientSignatureChecksum?: string;
    firebasePackageName?: string;
}

export const AppInfoBulkEdit: React.FC = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [appInfos, setAppInfos] = useState<AppInfo[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadAppInfos();
    }, []);

    const loadAppInfos = async () => {
        try {
            setLoading(true);
            const response = await appInfoService.getAll();
            if (response.status === OperationStatus.SUCCESS && response.data) {
                setAppInfos(response.data);
            } else {
                message.error(response.message || 'Failed to load app information');
            }
        } catch (error) {
            message.error('Failed to load app information');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: AppInfoBulkEditRequest) => {
        try {
            setLoading(true);
            const response = await appInfoService.bulkUpdate(values);

            if (response.status === OperationStatus.SUCCESS) {
                message.success('App info updated successfully');
                navigate('/dashboard/app-info/list');
            } else {
                message.error(response.message || 'Failed to update app info');
            }
        } catch (error) {
            message.error('Failed to update app info');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <Title level={4} style={{ marginBottom: 24 }}>
                Bulk Edit App Information
            </Title>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark
            >
                <Form.Item
                    label="App Names"
                    name="appNames"
                    rules={[
                        { required: true, message: 'Please select at least one app name' },
                        { type: 'array', min: 1, message: 'Please select at least one app name' }
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder="Select app names to edit"
                        loading={loading}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {appInfos.map((appInfo) => (
                            <Option key={appInfo.id} value={appInfo.appName}>
                                {appInfo.appName}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Version"
                    name="version"
                >
                    <Input placeholder="1.0" />
                </Form.Item>

                <Form.Item
                    label="Version Code"
                    name="versionCode"
                    rules={[
                        { type: 'number', min: 1, message: 'Version code must be greater than 0' }
                    ]}
                >
                    <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    label="Download Link"
                    name="downloadLink"
                    rules={[
                        { type: 'url', message: 'Please enter a valid URL' }
                    ]}
                >
                    <Input placeholder="https://example.com/app.apk" />
                </Form.Item>

                <Form.Item
                    label="Admin Component Name"
                    name="adminComponentName"
                >
                    <Input placeholder="com.example.app.admin" />
                </Form.Item>

                <Form.Item
                    label="Client Signature Checksum"
                    name="clientSignatureChecksum"
                >
                    <Input placeholder="SHA-256 checksum" />
                </Form.Item>

                <Form.Item
                    label="Firebase Restricted Package Name"
                    name="firebasePackageName"
                >
                    <Input placeholder="com.example.app.restricted" />
                </Form.Item>

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Update
                        </Button>
                        <Button onClick={() => navigate('/dashboard/app-info/list')}>
                            Cancel
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

